import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Checkbox, Icon, Menu } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, Can, CategoryLicenseModal, CustomConfirm } from "components";
import { useSelectedDealer } from "components/DealersAndLocationsDropdown/hooks";
import { usePrevious, useRegisterNavAction } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import {
  InvoiceModal,
  InvoiceTable,
  LocationInvoiceDisabledMessage,
  LocationLicenseNavbar,
  MonthlyTable,
  OneTimeTable,
  Remark,
  StandaloneLicenseModal
} from "modules/LocationLicenseOverview/components";
import { LocationLicenseOverviewFilters } from "modules/LocationLicenseOverview/components/LocationLicenseFilters";
import { useLocationLicensesDashboardFilters } from "modules/LocationLicenseOverview/components/LocationLicenseFilters/hooks";
import {
  downloadLocationLicenseInvoice,
  setPaginatedInvoices,
  useCategoryLicensesMutation,
  useInvoicesQuery,
  useItemsQuery,
  useLicensesQuery,
  useRemarkQuery,
  useSelectedStandaloneLicenceDeleteMutation,
  useUpdateRemarkMutation
} from "modules/LocationLicenseOverview/hooks";
import "modules/LocationLicenseOverview/LocationLicenseOverview.scss";

import type { EXACT_CATEGORIES, ROLES } from "models";
import type { HistoricalInvoice } from "modules/LocationLicenseOverview/queryKeys";
import type { CheckboxProps, MenuItemProps } from "semantic-ui-react";

export enum LOCATION_LICENSE_SUBPAGE {
  MonthlyLicenses = 0,
  OneTimeLicenses,
  Invoices
}

export type LicenseType = {
  id: number;
  code: string;
  name: string | null;
  price: number;
  category: string | null;
  categoryID: number | null;
  totalPrice: number;
  description: string | null;
  exact_item_id: string;
  role_id?: ROLES | null;
  quantity?: number;
  invoiceOn?: string | Date | null;
  dealer_location_id?: number;
  dealer_location_name?: string | null;
  license_category_type?: EXACT_CATEGORIES;
};

type Pane = {
  menuItem: string;
  content: React.ReactNode;
};

export const LocationLicenseOverview = () => {
  const { t } = useTranslation();

  const { selectedDealer } = useSelectedDealer();
  const { location: selectedLocation } = useLocationLicensesDashboardFilters();

  const { items, isLoadingItems } = useItemsQuery();
  const { data: remarkData } = useRemarkQuery(selectedLocation?.id);
  const { mutate: toggleCategoryLicenses } = useCategoryLicensesMutation();
  const { data: licensesData, isFetching: isLoadingLicenses, refetch: refetchLicenses } = useLicensesQuery();

  const { mutate: updateRemark } = useUpdateRemarkMutation();
  const { mutateAsync: deleteStandaloneLicense, isPending: isDeleteStandaloneLicensePending } = useSelectedStandaloneLicenceDeleteMutation();

  const [paginatedURL, setPaginatedURL] = useState("");
  const [currentInvoicePage, setCurrentInvoicePage] = useState(0);
  const [currentLocationLicenseSubpage, setCurrentLocationLicenseSubpage] = useState<LOCATION_LICENSE_SUBPAGE>(LOCATION_LICENSE_SUBPAGE.MonthlyLicenses);

  const [invoicePDF, setInvoicePDF] = useState<Blob | null>(null);
  const [selectedLicense, setSelectedLicense] = useState<LicenseType | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<HistoricalInvoice | null>(null);
  const [showCategoryLicenseModal, setShowCategoryLicenseModal] = useState(false);
  const [showStandaloneLicenseModal, setShowStandaloneLicenseModal] = useState(false);
  const [selectedDeleteStandaloneLicense, setSelectedDeleteStandaloneLicense] = useState<number | null>(null);

  const previousLocation = usePrevious(selectedLocation);
  const {
    invoices,
    isLoadingInvoices,
    paginatedInvoices,
    refetch: refetchInvoices,
    isRefetching: isRefetchingInvoices
  } = useInvoicesQuery(paginatedURL, currentInvoicePage);

  useRegisterNavAction(() => {
    if (currentLocationLicenseSubpage === LOCATION_LICENSE_SUBPAGE.Invoices) {
      setPaginatedInvoices({ data: [] });
      refetchInvoices();
      return;
    }

    refetchLicenses();
  });

  const handleToggleCategoryLicenses = (checked?: boolean) => {
    if (selectedLocation) toggleCategoryLicenses({ dealer_location_id: selectedLocation.id, enabled: !!checked });
  };

  const handleDeleteLicense = (standalone_license_id: number) => {
    setSelectedDeleteStandaloneLicense(standalone_license_id);
  };

  const handleConfirmDeleteLicense = async () => {
    if (selectedDeleteStandaloneLicense) await deleteStandaloneLicense({ standalone_license_id: selectedDeleteStandaloneLicense });
    setSelectedDeleteStandaloneLicense(null);
  };

  const handleCancelDeleteLicense = () => {
    setSelectedDeleteStandaloneLicense(null);
  };

  const onLicenseRowClick = (rowData: LicenseType) => {
    setSelectedLicense(rowData);

    if (rowData.license_category_type) setShowCategoryLicenseModal(true);
    else setShowStandaloneLicenseModal(true);
  };

  const onInvoiceRowClick = async (rowData: HistoricalInvoice) => {
    if (!selectedDealer?.id) return;

    setSelectedInvoice(rowData);
    try {
      const pdfData = await downloadLocationLicenseInvoice({ document_id: rowData.ID, dealer_location_id: selectedLocation?.id ?? 0 });
      setInvoicePDF(pdfData);
    } catch {
      toast.error(t("v8_failed_to_open_invoice", "Failed to open invoice"));
    }
  };

  const newStandaloneLicense = () => {
    setSelectedLicense(null);
    setShowStandaloneLicenseModal(true);
  };

  const handleChangeTab = (_e: React.MouseEvent<HTMLAnchorElement>, { index }: MenuItemProps) => {
    setCurrentLocationLicenseSubpage(index as LOCATION_LICENSE_SUBPAGE);
  };

  const handleCloseInvoiceModal = () => {
    setInvoicePDF(null);
    setSelectedInvoice(null);
  };

  const invoicePageChange = (pageIndex: number) => {
    if (pageIndex < 0) return;

    const existingLocationData = paginatedInvoices.data.find(element => element.location === selectedLocation?.id);
    if (existingLocationData && Boolean(existingLocationData.data[pageIndex])) return setCurrentInvoicePage(pageIndex);

    if (!invoices?.__next) return;
    setPaginatedURL(invoices.__next);
    setCurrentInvoicePage(pageIndex);
  };

  const handleResetState = () => {
    setPaginatedURL("");
    setCurrentInvoicePage(0);
    setCurrentLocationLicenseSubpage(LOCATION_LICENSE_SUBPAGE.MonthlyLicenses);
    setPaginatedInvoices({ data: [] });
  };

  const handleUpdateRemark = (remark: string) => updateRemark({ remark, dealer_location_id: selectedLocation?.id });

  useEffect(() => {
    if (selectedLocation && previousLocation && selectedLocation.id !== previousLocation.id) {
      handleResetState();
    }
  }, [selectedLocation, previousLocation]);

  const tabPanes: Pane[] = [
    {
      menuItem: t("v8_monthly", "Monthly"),
      content: (
        <MonthlyTable
          items={items}
          licenses={licensesData}
          isLoading={isLoadingLicenses}
          isLoadingItems={isLoadingItems}
          onLicenseRowClick={onLicenseRowClick}
          handleDeleteLicense={handleDeleteLicense}
        />
      )
    },
    {
      menuItem: t("v8_once", "Once"),
      content: (
        <OneTimeTable
          items={items}
          licenses={licensesData}
          isLoading={isLoadingLicenses}
          isLoadingItems={isLoadingItems}
          onLicenseRowClick={onLicenseRowClick}
          handleDeleteLicense={handleDeleteLicense}
        />
      )
    },
    {
      menuItem: t("v8_invoice_history", "Invoice History"),
      content: (
        <InvoiceTable
          invoices={invoices}
          isLoadingInvoices={isLoadingInvoices}
          isRefetchingInvoices={isRefetchingInvoices}
          currentInvoicePage={currentInvoicePage}
          onPageChange={invoicePageChange}
          onInvoiceRowClick={onInvoiceRowClick}
        />
      )
    }
  ];

  return (
    <div className="LocationLicenseOverview mt-20">
      {!selectedLocation?.is_exact_invoicing_enabled && <LocationInvoiceDisabledMessage />}
      <LocationLicenseOverviewFilters />

      {selectedLocation?.is_exact_invoicing_enabled && (
        <>
          <LocationLicenseNavbar items={items} licensesData={licensesData} />
          <div className="LocationLicenseOverview-Header">
            <div className="LocationLicenseOverview-Header-Tabs">
              <Can I={ACL_ACTIVITIES.Create} the={ACL_RESOURCES.ExactLicense}>
                <Button basic icon className="add-btn" onClick={newStandaloneLicense}>
                  <Icon className="plus" />
                </Button>
              </Can>
              <Menu secondary>
                {tabPanes.map((pane, index) => (
                  <Menu.Item
                    key={index}
                    name={pane.menuItem}
                    className="location-overview-header-tab"
                    active={currentLocationLicenseSubpage === index}
                    index={index}
                    onClick={handleChangeTab}
                  />
                ))}
                {remarkData && <Remark remarkData={remarkData} onUpdateRemark={handleUpdateRemark} />}
              </Menu>
            </div>
            <Can I={ACL_ACTIVITIES.Update} the={ACL_RESOURCES.ExactLicenseCategory}>
              <div className="LocationLicenseOverview-toggle-container">
                <span> {t("v8_role_license_service", "Role / License / Service")} </span>
                <Checkbox
                  toggle
                  className="checkbox-automatic-licenses"
                  defaultChecked={selectedLocation.exact_is_category_licence_enabled}
                  onChange={(_, { checked }: CheckboxProps) => handleToggleCategoryLicenses(checked)}
                />
              </div>
            </Can>
          </div>

          <div className="LocationLicenseOverview-container mt-20">{tabPanes[currentLocationLicenseSubpage].content}</div>
          <StandaloneLicenseModal
            isOpen={showStandaloneLicenseModal}
            onClose={() => setShowStandaloneLicenseModal(false)}
            selectedLicense={selectedLicense}
            items={items}
            selectedTab={currentLocationLicenseSubpage}
            isLoadingLicenses={isLoadingLicenses}
          />
          <CategoryLicenseModal
            isOpen={showCategoryLicenseModal}
            onClose={() => setShowCategoryLicenseModal(false)}
            selectedLicense={selectedLicense}
            items={items}
            selectedTab={currentLocationLicenseSubpage}
            isLoadingLicenses={isLoadingLicenses}
            licensesData={licensesData}
            locationId={selectedLocation.id}
          />
          <InvoiceModal
            isOpen={!!(invoicePDF && selectedInvoice)}
            onClose={handleCloseInvoiceModal}
            invoice={selectedInvoice as HistoricalInvoice}
            invoicePDF={invoicePDF}
          />
          <CustomConfirm
            type={CUSTOM_CONFIRM_TYPES.Danger}
            handleCancel={handleCancelDeleteLicense}
            isOpen={!!selectedDeleteStandaloneLicense}
            isLoading={isDeleteStandaloneLicensePending}
            confirmMsg={t("v8_delete_confirm_message", "Are you sure that you want to delete this? You can't undo this action.")}
            handleConfirm={handleConfirmDeleteLicense}
          />
        </>
      )}
    </div>
  );
};
